import type { DAS1Result } from '@domain/fiscalite/DASService';
import type { Tenant } from '@shared/types/Tenant';
import { fmtNumber, esc } from '@presentation/shell/format';
import { nxBannerLeftHtml } from '@presentation/print/documentBrand';

export interface DAS1Document {
  title: string;
  filename: string;
  html: string;
}

/**
 * Document imprimable de la DAS 1 (Déclaration Annuelle des Salaires),
 * à transmettre à la DGI avant le 30 avril N+1 (CGI Gabon).
 * Réutilise la charte documentaire des attestations (classes `nx-*`).
 */
export function buildDAS1HTML(das: DAS1Result, t: Tenant | null): DAS1Document {
  const today = new Date().toLocaleDateString('fr-FR');
  const ville = t?.ville || 'Libreville';
  const refDoc = `DAS1-${das.annee}`;
  const totalCnss = das.lignes.reduce((s, l) => s + l.cnss_sal_annuel, 0);
  const totalCnamgs = das.lignes.reduce((s, l) => s + l.cnamgs_sal_annuel, 0);

  const corps = das.lignes
    .map(
      (l) => `<tr>
        <td style="font-family:'Courier New',monospace;">${esc(l.matricule)}</td>
        <td>${esc(l.nom_complet)}</td>
        <td class="num">${fmtNumber(l.brut_annuel)}</td>
        <td class="num">${fmtNumber(l.irpp_annuel)}</td>
        <td class="num">${fmtNumber(l.cnss_sal_annuel)}</td>
        <td class="num">${fmtNumber(l.cnamgs_sal_annuel)}</td>
      </tr>`,
    )
    .join('');

  const html = `<div class="nx-attestation nx-salaire" id="nx-das1-print">
    <div class="nx-banner">
      ${nxBannerLeftHtml()}
      <div class="nx-banner-right">
        <div class="nx-co">${esc(t?.nom || 'Entreprise')}</div>
        <div class="nx-co-meta">${esc(t?.adresse || '')}<br>${esc(t?.ville || '')}${t?.telephone ? ' · ' + esc(t.telephone) : ''}<br>NIF ${esc(t?.nif || '—')} · RCCM ${esc(t?.rccm || '—')}</div>
      </div>
    </div>
    <div class="nx-doc-ref"><div>Réf. document <span class="nx-ref-num">${esc(refDoc)}</span></div><div>Édité le ${esc(today)}</div></div>
    <div class="nx-body">
      <h1 class="nx-title">DÉCLARATION ANNUELLE DES SALAIRES</h1>
      <div class="nx-subtitle">DAS 1 — Exercice ${esc(das.annee)}</div>
      <div class="nx-content">
        <p>La société <strong>${esc(t?.nom || 'la société')}</strong>, immatriculée au NIF n° <strong>${esc(t?.nif || '—')}</strong>, déclare les rémunérations versées à son personnel au titre de l'exercice <strong>${esc(das.annee)}</strong>, ainsi que les retenues opérées sur celles-ci.</p>
        <div class="nx-highlight-cards">
          <div class="nx-highlight-card"><div class="nx-hc-label">Effectif déclaré</div><div class="nx-hc-value">${das.lignes.length}</div><div class="nx-hc-sub">salarié(s)</div></div>
          <div class="nx-highlight-card cyan"><div class="nx-hc-label">Brut annuel</div><div class="nx-hc-value">${fmtNumber(das.total_brut)}</div><div class="nx-hc-sub">FCFA</div></div>
          <div class="nx-highlight-card menthe"><div class="nx-hc-label">IRPP retenu</div><div class="nx-hc-value">${fmtNumber(das.total_irpp)}</div><div class="nx-hc-sub">FCFA</div></div>
        </div>
        <p style="margin-top:18px; font-weight:700; color:var(--nx-marine);">État nominatif des rémunérations:</p>
        <table class="nx-amounts">
          <thead><tr><th>Matricule</th><th>Salarié</th><th class="num">Brut annuel</th><th class="num">IRPP</th><th class="num">CNSS sal.</th><th class="num">CNAMGS sal.</th></tr></thead>
          <tbody>
            ${corps || '<tr><td colspan="6">Aucun salarié rémunéré sur l\'exercice.</td></tr>'}
            <tr class="nx-total"><td colspan="2"><strong>TOTAUX</strong></td><td class="num"><strong>${fmtNumber(das.total_brut)}</strong></td><td class="num"><strong>${fmtNumber(das.total_irpp)}</strong></td><td class="num"><strong>${fmtNumber(totalCnss)}</strong></td><td class="num"><strong>${fmtNumber(totalCnamgs)}</strong></td></tr>
          </tbody>
        </table>
        <div class="nx-legal-mention"><strong>Obligation déclarative:</strong> la DAS 1 doit être déposée auprès de la Direction Générale des Impôts au plus tard le <strong>30 avril ${Number(das.annee) + 1}</strong>. Montants exprimés en FCFA.</div>
      </div>
      <div class="nx-signature"><div class="nx-date">Fait à <strong>${esc(ville)}</strong>,<br>le <strong>${esc(today)}</strong></div><div class="nx-sign-block"><div class="nx-sign-line">La Direction</div><div class="nx-sign-role">Représentant légal</div></div></div>
    </div>
    <div class="nx-footer">
      <div><span class="nx-foot-co">${esc(t?.nom || '')}</span> &nbsp;·&nbsp; ${esc(t?.email || '')} &nbsp;·&nbsp; ${esc(t?.telephone || '')}</div>
      <div>Page 1/1 &nbsp;·&nbsp; Doc. ${esc(refDoc)}</div>
    </div>
  </div>`;

  return { title: `DAS 1 — Exercice ${das.annee}`, filename: `das1_${das.annee}.pdf`, html };
}
